// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
    Form, Modal, notification, Button,
} from 'antd';
import Text from 'antd/lib/typography/Text'

import { CombinedState, StorageLocation } from 'reducers';
import { exportActions, exportDatasetAsync } from 'actions/export-actions';
import {
    Dumper, Job, Project, Storage, StorageData, Task,
} from 'cvat-core-wrapper';

import { getClientHost } from 'shared/constants';

import { YoloAugmentationConfig } from 'proto/nn_training/DetectorTraining_pb';
import { CreateYoloDatasetParams, DatasetFormat, Dataset } from 'proto/c60/nn/center/DataSets_pb';

import { ObjectRef } from 'proto/Common_pb';
import { DatasetsServiceClient } from 'proto/c60/nn/center/DataSetsServiceClientPb';
import { createEnhancedClient } from 'utils/grpc';

import CreateDataset from 'components/export-dataset/create-dataset';
import ExportDataset from 'components/export-dataset/export-dataset';

function mapSelectedFormatToEnum(selectedFormat: string | undefined): DatasetFormat | undefined {
    const selectedRaw = (selectedFormat || '').toString();
    const selectedUpper = selectedRaw.toUpperCase().trim();

    // Подробная трассировка выбора формата
    // eslint-disable-next-line no-console
    console.log('[ExportDataset] Selected format from UI:', selectedRaw);

    // Явное сопоставление человекочитаемых названий с enum
    const explicitMap: Record<string, DatasetFormat> = {
        'YOLO 1.1': DatasetFormat.YOLO1_1,
        'YOLO1_1': DatasetFormat.YOLO1_1,
        'AIM 1.0': DatasetFormat.AIM1,
        'AIM1': DatasetFormat.AIM1,
    };

    if (Object.prototype.hasOwnProperty.call(explicitMap, selectedRaw)) {
        const value = explicitMap[selectedRaw];
        // eslint-disable-next-line no-console
        console.log('[ExportDataset] Matched format via explicitMap:', selectedRaw, '->', DatasetFormat[value], value);
        return value;
    }

    // Нормализуем строку: оставляем только буквы и цифры в верхнем регистре
    const normalize = (s: string): string => s
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');

    const normalizedSelected = normalize(selectedUpper);
    const enumKeys = Object.keys(DatasetFormat)
        .filter((key) => Number.isNaN(Number(key))) as (keyof typeof DatasetFormat)[];

    // eslint-disable-next-line no-console
    console.log('[ExportDataset] Normalized selected format:', normalizedSelected);
    // eslint-disable-next-line no-console
    console.log('[ExportDataset] Available DatasetFormat keys:', enumKeys);

    // Попытка 1: совпадение по нормализованной строке целиком (подстроки)
    for (const key of enumKeys) {
        const normalizedKey = normalize(key);
        // eslint-disable-next-line no-console
        console.log('[ExportDataset] Compare normalized:', normalizedSelected, 'vs', normalizedKey);
        if (normalizedSelected && (normalizedSelected.includes(normalizedKey) || normalizedKey.includes(normalizedSelected))) {
            const value = DatasetFormat[key];
            // eslint-disable-next-line no-console
            console.log('[ExportDataset] Matched format by normalized substring:', selectedRaw, '->', key, value);
            return value;
        }
    }

    // Попытка 2: берём только буквенную приставку до первой цифры, без регистра
    const firstTokenLetters = selectedRaw.match(/^[A-Za-z]+/);
    const token = firstTokenLetters ? firstTokenLetters[0].toLowerCase() : '';
    // eslint-disable-next-line no-console
    console.log('[ExportDataset] First letter token from selected:', token);

    if (token) {
        for (const key of enumKeys) {
            const keyTokenMatch = key.match(/^[A-Z]+/);
            const keyToken = keyTokenMatch ? keyTokenMatch[0].toLowerCase() : '';
            // eslint-disable-next-line no-console
            console.log('[ExportDataset] Compare token:', token, 'vs', keyToken, 'for key', key);
            if (token === keyToken) {
                const value = DatasetFormat[key];
                // eslint-disable-next-line no-console
                console.log('[ExportDataset] Matched format by token:', selectedRaw, '->', key, value);
                return value;
            }
        }
    }

    // eslint-disable-next-line no-console
    console.warn('[ExportDataset] Could not map selected format to DatasetFormat enum. Selected:', selectedRaw);
    return undefined;
}

type FormValues = {
    selectedFormat: string | undefined;
    saveImages: boolean;
    customName: string | undefined;
    targetStorage: StorageData;
    useProjectTargetStorage: boolean;
    exportDatasetParams: Dataset.AsObject;
};

const initialValues: FormValues = {
    selectedFormat: undefined,
    saveImages: false,
    customName: undefined,
    targetStorage: {
        location: StorageLocation.LOCAL,
        cloudStorageId: undefined,
    },
    useProjectTargetStorage: true,
    exportDatasetParams: {},
};

function ExportDatasetModal(props: StateToProps): JSX.Element {
    const { dumpers, instance, current } = props;

    const currentHost = getClientHost();

    const [form] = Form.useForm();

    // Формат датасета
    const [datasetFormat, setDatasetFormat] = useState<string>('AIM 1.0');

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('[ExportDatasetModal] datasetFormat state changed:', datasetFormat);
    }, [datasetFormat]);

    useEffect(() => {
        // синхронизируем значение формата в state с полем формы
        form.setFieldsValue({ selectedFormat: datasetFormat });
        // eslint-disable-next-line no-console
        console.log('[ExportDatasetModal] sync Form selectedFormat <- datasetFormat:', datasetFormat);
    }, [datasetFormat, form]);

    const [instanceType, setInstanceType] = useState('');

    const [useDefaultTargetStorage, setUseDefaultTargetStorage] = useState(true);
    const [targetStorage, setTargetStorage] = useState<StorageData>({
        location: StorageLocation.LOCAL,
    });
    const [defaultStorageLocation, setDefaultStorageLocation] = useState(StorageLocation.LOCAL);
    const [defaultStorageCloudId, setDefaultStorageCloudId] = useState<number>();

    // Индикация загрузки на кнопке Создать, во время выполнения запроса gRPC
    const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

    const backState = useSelector((state: CombinedState) => state);
    const dispatch = useDispatch();

    useEffect(() => {
        if (instance instanceof Project) {
            setInstanceType(`проекта ${instance.name}`);
        } else if (instance instanceof Task || instance instanceof Job) {
            if (instance instanceof Task) {
                setInstanceType(`задачи ${instance.name}`);
            } else {
                setInstanceType(`разметки #${instance.id}`);
            }
            if (instance.mode === 'interpolation' && instance.dimension === '2d') {
                form.setFieldsValue({ selectedFormat: 'CVAT for video 1.1' });
            } else if (instance.mode === 'annotation' && instance.dimension === '2d') {
                form.setFieldsValue({ selectedFormat: 'CVAT for images 1.1' });
            }
        }
    }, [instance]);

    useEffect(() => {
        if (instance) {
            setDefaultStorageLocation(instance.targetStorage.location);
            setDefaultStorageCloudId(instance.targetStorage.cloudStorageId);
            // eslint-disable-next-line no-console
            console.log('[ExportDatasetModal] instance changed, targetStorage:', instance.targetStorage);
        }
    }, [instance]);

    const closeModal = (): void => {
        setUseDefaultTargetStorage(true);
        setTargetStorage({ location: StorageLocation.LOCAL });
        form.resetFields();
        dispatch(exportActions.closeExportDatasetModal(instance));
        // eslint-disable-next-line no-console
        console.log('[ExportDatasetModal] closeModal: reset datasetFormat to YOLO 1.1');
        setDatasetFormat('YOLO 1.1');
    };

    const handleExport = useCallback(
        (values: FormValues): void => {
            dispatch(
                exportDatasetAsync(
                    instance,
                    values.selectedFormat as string,
                    values.saveImages,
                    useDefaultTargetStorage,
                    useDefaultTargetStorage ?
                        new Storage({
                            location: defaultStorageLocation,
                            cloudStorageId: defaultStorageCloudId,
                        }) :
                        new Storage(targetStorage),
                    values.customName ? `${values.customName}.zip` : undefined,
                ),
            );
            closeModal();
            const resource = values.saveImages ? 'Датасета' : 'Аннотации';
            notification.info({
                message: `Запущен экспорт ${resource}`,
                description:
                    `Запущен экспорт ${resource} для ${instanceType}. ` +
                    `Загрузка ${resource} начнется, когда архив будет готов.`,
                className: `cvat-notification-notice-export-${instanceType.split(' ')[0]}-start`,
            });
        },
        [instance, instanceType, useDefaultTargetStorage, defaultStorageLocation, defaultStorageCloudId, targetStorage],
    );

    const handleCreate = useCallback(
        (values: FormValues): void => {
            // eslint-disable-next-line no-console
            console.log('[ExportDatasetModal] handleCreate form values:', values);

            const chosenFormat = mapSelectedFormatToEnum(values.selectedFormat);

            // Подробная трассировка результата сопоставления формата
            // eslint-disable-next-line no-console
            console.log('[ExportDataset] Final chosen DatasetFormat:', chosenFormat,
                chosenFormat !== undefined ? DatasetFormat[chosenFormat] : 'undefined');

            // Добавляем в initialValues информацию по название датасета для создания Датасета
            initialValues.exportDatasetParams.title = values.customName?.trim();

            // Вычисляем общее количество изображений в Проекте
            const imageCount = backState.tasks.current.reduce(
                (accumulator, currentValue) => accumulator + currentValue.size,
                0,
            );

            // Если проект не содержит изображения - выводим ошибку ELSE Создаем датасет
            if (imageCount === 0) {
                notification.error({
                    message: 'Создание нового датасета',
                    description: 'Ошибка создания датасета: нет ни одного изображения.',
                });
            } else {
                const DatasetParams = initialValues.exportDatasetParams;
                const DatasetAugmentation = initialValues.exportDatasetParams.augmentation;

                // new YoloAugmentationConfig()
                const augmentation = DatasetAugmentation ?
                    new YoloAugmentationConfig()
                        .setHsvH(DatasetAugmentation.hsvH)
                        .setHsvS(DatasetAugmentation.hsvS)
                        .setHsvV(DatasetAugmentation.hsvV)
                        .setDegrees(DatasetAugmentation.degrees)
                        .setTranslate(DatasetAugmentation.translate)
                        .setScale(DatasetAugmentation.scale)
                        .setShear(DatasetAugmentation.shear)
                        .setPerspective(DatasetAugmentation.perspective)
                        .setFlipud(DatasetAugmentation.flipud)
                        .setFliplr(DatasetAugmentation.fliplr)
                        .setMosaic(DatasetAugmentation.mosaic)
                        .setMixup(DatasetAugmentation.mixup)
                        .setCopyPaste(DatasetAugmentation.copyPaste) :
                    undefined;

                // new Dataset()
                const datasetConfig = new Dataset()
                    .setTitle(String(DatasetParams.title))
                    .setFormat(chosenFormat)
                    .setAuthortitle(String(DatasetParams.authortitle))
                    .setCvatAuthorId(Number(DatasetParams.cvatAuthorId))
                    .setCvatProjectId(Number(DatasetParams.cvatProjectId))
                    .setCvatProjectname(String(DatasetParams.cvatProjectname))
                    .setAugmentation(augmentation);
                if (DatasetParams.organization) {
                    datasetConfig.setOrganization(
                        new ObjectRef()
                            .setId(Number(DatasetParams.organization?.id))
                            .setTitle(String(DatasetParams.organization?.title)),
                    );
                }

                if (
                    Object.prototype.hasOwnProperty.call(DatasetParams, 'trainsetimages') &&
                    Object.prototype.hasOwnProperty.call(DatasetParams, 'validationsetimages') &&
                    Object.prototype.hasOwnProperty.call(DatasetParams, 'testsetimages')
                ) {
                    datasetConfig
                        .setTrainsetimages(DatasetParams.trainsetimages!)
                        .setValidationsetimages(DatasetParams.validationsetimages!)
                        .setTestsetimages(DatasetParams.testsetimages!);
                }

                // ! Создаем датасет gRPC
                (async () => {
                    setConfirmLoading(true);

                    if (currentHost) {
                        const client = createEnhancedClient(DatasetsServiceClient);
                        await client
                            .createYoloDatasetFromProject(
                                new CreateYoloDatasetParams()
                                    .setCvatprojectid(Number(DatasetParams.cvatProjectId))
                                    .setDatasetconfig(datasetConfig),
                                {},
                            )
                            .then((data) => {
                                const err = data.getError();
                                if (err) {
                                    setConfirmLoading(false);
                                    notification.error({
                                        message: 'Создание нового датасета',
                                        description: err,
                               });
                                    return;

                                }
                                setConfirmLoading(false);
                                notification.success({
                                    message: 'Создание нового датасета',
                                    description: 'Датасет был успешно создан.',
                                });

                                closeModal();
                            })
                            .catch((error) => {
                                setConfirmLoading(false);

                                notification.error({
                                    message: 'Создание нового датасета',
                                    description: 'Ошибка создания датасета.',
                                });

                                console.error(error);
                            });
                    }
                })();

                notification.info({
                    message: 'Создание нового датасета',
                    description:
                        'Запущен процесс создание нового датасета. Не закрывайте ' +
                        'страницу, пока не появится сообщение о завершении процесса.',
                });
            }
        },
        [instance, instanceType, useDefaultTargetStorage, defaultStorageLocation, defaultStorageCloudId, targetStorage],
    );

    return (
        <Modal
            title={<Text strong>{`Экспортировать датасет ${instanceType}`}</Text>}
            open={!!instance}
            onCancel={closeModal}
            onOk={() => form.submit()}
            footer={
                datasetFormat === 'YOLO 1.1' || datasetFormat === 'AIM 1.0' ?
                    [
                        <Button key='Ok' type='primary' onClick={() => form.submit()} loading={confirmLoading}>
                            Создать
                        </Button>,
                        <Button key='Cancel' onClick={closeModal}>
                            Отменить
                        </Button>,
                    ] :
                    [
                        <Button key='Ok' type='primary' onClick={() => form.submit()}>
                            Экспортировать
                        </Button>,
                        <Button key='Cancel' onClick={closeModal}>
                            Отменить
                        </Button>,
                    ]
            }
            className={`cvat-modal-export-${instanceType.split(' ')[0]}`}
            destroyOnClose
            width={1124}
            afterClose={() => {
                setDatasetFormat('AIM 1.0');
                setConfirmLoading(false);
            }}
        >
            {datasetFormat === 'YOLO 1.1' || datasetFormat === 'AIM 1.0' ? (
                <CreateDataset
                    form={form}
                    initialValues={initialValues}
                    handleCreate={handleCreate}
                    dumpers={dumpers}
                    instance={instance}
                    current={current}
                    datasetFormat={datasetFormat}
                    setDatasetFormat={setDatasetFormat}
                    backState={backState}
                />
            ) : (
                <ExportDataset
                    form={form}
                    initialValues={initialValues}
                    handleExport={handleExport}
                    dumpers={dumpers}
                    instance={instance}
                    current={current}
                    datasetFormat={datasetFormat}
                    setDatasetFormat={setDatasetFormat}
                />
            )}
        </Modal>
    );
}

interface StateToProps {
    dumpers: Dumper[];
    instance: Project | Task | Job | null;
    current: string[];
}

function mapStateToProps(state: CombinedState): StateToProps {
    const { instanceType } = state.export;
    const instance = !instanceType ?
        null :
        state.export[`${instanceType}s` as 'projects' | 'tasks' | 'jobs'].dataset.modalInstance;

    return {
        instance,
        current: !instanceType ?
            [] :
            state.export[`${instanceType}s` as 'projects' | 'tasks' | 'jobs'].dataset.current[instance.id],
        dumpers: state.formats.annotationFormats.dumpers,
    };
}

export default connect(mapStateToProps)(ExportDatasetModal);
